import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { BiEnvelope } from 'react-icons/bi';
import { FaXmark } from 'react-icons/fa6';
import { HiPaperAirplane } from 'react-icons/hi';
import { AiOutlineMail } from "react-icons/ai";
import { FiPhone } from "react-icons/fi";

import { suggestions } from "utility/api";
import { callApi } from "utility/axios";
import toast from "react-hot-toast";

const FloatingContactIcon = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters')
      .required('Name is required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    // mobile: Yup.string()
    //   .matches(/^[+]?[\d\s\-\(\)]{10,15}$/, 'Please enter a valid mobile number'),
    mobile: Yup.string()
    .matches(/^[+]?[\d\s\-()]{10,15}$/, 'Please enter a valid mobile number')
    .min(10, 'Mobile number must be at least 10 digits'),
    suggestion: Yup.string()
      .max(500, 'Suggestion must be less than 500 characters')
  });

  const initialValues = {
    name: '',
    email: '',
    mobile: '',
    suggestion: ''
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
    const request = {
      ...suggestions,
      payload: values,
    };
    await toast.promise(callApi(request), {
      loading: "Sending your suggestions...",
      success: (data) => {
        resetForm();
        setIsOpen(false);
        return data?.message || 'Thank you for your suggestion! We will get back to you soon.';
      },
      error: 'Something went wrong. Please try again.',
    });
    } catch (error) {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openPopup = () => setIsOpen(true);
  const closePopup = () => setIsOpen(false);

  return (
    <>
      {/* Floating Contact Icon */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40">
        <button
          className="group relative bg-white border-2 border-gray-300 hover:border-gray-400 rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={openPopup}
          aria-label="Contact us"
          name="suggestion"
        >
          <BiEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-gray-900 transition-colors duration-300" />

          {/* Tooltip - Hidden on mobile */}
          <div
            className={`absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-black bg-white text-sm rounded-lg shadow-lg whitespace-nowrap transition-all duration-300 hidden sm:block ${
              isHovered 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-2 pointer-events-none'
            }`}
          >
            Suggestions
            <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
          </div>

          {/* Pulse Animation Ring */}
          <div className="absolute inset-0 rounded-full border-2 border-gray-400 animate-ping opacity-30"></div>
          <div className="absolute inset-0 rounded-full border-2 border-white animate-ping opacity-50"></div>
        </button>
      </div>

      {/* Modal Dialog */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePopup}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Close Button */}
                  <button
                    onClick={closePopup}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200 rounded-full p-1 hover:bg-gray-100"
                  >
                    <FaXmark className="w-6 h-6" />
                  </button>

                  {/* Header */}
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 pr-8"
                  >
                    We'd Love Your Suggestions
                  </DialogTitle>

                  {/* Contact Information */}
                  <div className="bg-black/5 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">For Direct Contact:</h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <AiOutlineMail className="w-4 h-4 mr-2 text-gray-500" />
                        <a href="mailto:info@arjiv.com" className="text-sm hover:text-gray-800 transition-colors break-all">
                          info@arjiv.com
                        </a>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <FiPhone className="w-4 h-4 mr-2 text-gray-500" />
                        <a href="tel:+919898300984" className="text-sm hover:text-gray-800 transition-colors">
                          +91 98983 00984
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Formik Form */}
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Name Field */}
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            className="w-full px-3 py-2 border border-primary/30 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent text-gray-800 text-sm sm:text-base"
                            // placeholder="Your full name"
                          />
                          <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Email Field */}
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="email"
                            id="email"
                            name="email"
                            className="w-full px-3 py-2 border border-primary/30 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent text-gray-800 text-sm sm:text-base"
                            // placeholder="your.email@example.com"
                          />
                          <ErrorMessage name="email" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Mobile Field */}
                        <div>
                          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                            Mobile
                          </label>
                          <Field
                            type="number"
                            id="mobile"
                            name="mobile"
                            className="w-full px-3 py-2 border border-primary/30 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent text-gray-800 text-sm sm:text-base"
                            // placeholder="Your mobile number"
                          />
                          <ErrorMessage name="mobile" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Suggestion Field */}
                        <div>
                          <label htmlFor="suggestion" className="block text-sm font-medium text-gray-700 mb-2">
                            Suggestions
                          </label>
                          <Field
                            as="textarea"
                            id="suggestion"
                            name="comment"
                            rows="3"
                            className="w-full px-3 py-2 border border-primary/30 placeholder:font-thin rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent text-gray-800 resize-none text-sm sm:text-base"
                            // placeholder="Share your thoughts and suggestions with us..."
                          />
                          <ErrorMessage name="suggestion" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Submit Buttons */}
                        <div className="mt-6 flex gap-3">
                          <button
                            type="button"
                            onClick={closePopup}
                            className="flex-1 px-4 py-2 border border-primary/50 text-gray-700 rounded-md hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 font-medium text-sm sm:text-base"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 text-white px-4 py-2 rounded-md hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center justify-center text-sm sm:text-base"
                            style={{ backgroundColor: '#212121' }}
                          >
                            {isSubmitting ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            ) : (
                              <>
                                <HiPaperAirplane className="w-4 h-4 mr-2" />
                                Submit
                              </>
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </DialogPanel>
              </TransitionChild>
              </div>
            </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default FloatingContactIcon;